import { ReactNode, useMemo } from 'react';

import { GrfqTrade } from '#/unified-rfqs/entities/grfq/domain';
import { Trade as DrfqTrade } from '#/unified-rfqs/entities/drfq/trades';
import { Drfq } from '#/unified-rfqs/entities/drfq/domain';

import { WebSocketHandlersArgs } from '#/unified-rfqs/services/shared';

import { usePinnedRfqsAction } from '#/unified-rfqs/ui/rfqs/pinned-rfqs-context';
import { UnifiedRfqsServiceProvider } from '#/unified-rfqs/contexts/unified-rfqs';
import { GrfqServiceProvider } from '#/unified-rfqs/contexts/grfqs';
import { DrfqDependenciesProvider } from '#/unified-rfqs/contexts/drfqs';
import { CounterpartiesServiceProvider } from '#/unified-rfqs/contexts/counterparties';
import { Order } from '#/unified-rfqs/entities/drfq/orders';
import {
  UnifiedOpenOrdersServiceProvider,
  UnifiedOrdersServiceProvider,
} from './unified-orders';
import createStores from './create-stores';
import { UnifiedTradesServiceProvider } from './unified-trades';

interface UnifiedRfqsHandlers
  extends WebSocketHandlersArgs<GrfqTrade | DrfqTrade> {
  readonly onDrfqClosed: (drfq: Drfq) => void;
  readonly onDrfqOrderCanceled: (order: Order) => void;
  /** Optional — only fires for async-settlement venues (Paradex). */
  readonly onTradeSubmitted?: (trade: DrfqTrade) => void;
}
interface WebSocketProps {
  readonly children: ReactNode;
  readonly handlers: UnifiedRfqsHandlers;
}

export function UnifiedRfqServicesProvider({
  children,
  handlers,
}: WebSocketProps) {
  const { pinRfq } = usePinnedRfqsAction();
  const {
    drfqOrderbooksStore,
    drfqOrdersStore,
    drfqHistoryStore,
    drfqDraftsStore,
    rfqStore,
    ordersStore,
    grfqQuoteStore,
    tradesStore,
    tradeTapeStore,
  } = useMemo(() => createStores(), []);

  return (
    <GrfqServiceProvider
      handlers={handlers}
      rfqStore={rfqStore}
      grfqQuoteStore={grfqQuoteStore}
      ordersStore={ordersStore}
      tradesStore={tradesStore}
      tradeTapeStore={tradeTapeStore}
    >
      <CounterpartiesServiceProvider>
        <DrfqDependenciesProvider
          handlers={handlers}
          pinRfq={pinRfq}
          rfqStore={rfqStore}
          drfqOrdersStore={drfqOrdersStore}
          drfqOrderbooksStore={drfqOrderbooksStore}
          drfqHistoryStore={drfqHistoryStore}
          drfqDraftsStore={drfqDraftsStore}
          ordersStore={ordersStore}
          tradesStore={tradesStore}
          tradeTapeStore={tradeTapeStore}
        >
          <UnifiedOrdersServiceProvider ordersStore={ordersStore}>
            <UnifiedOpenOrdersServiceProvider ordersStore={ordersStore}>
              <UnifiedRfqsServiceProvider rfqsStore={rfqStore}>
                <UnifiedTradesServiceProvider tradesStore={tradesStore}>
                  {children}
                </UnifiedTradesServiceProvider>
              </UnifiedRfqsServiceProvider>
            </UnifiedOpenOrdersServiceProvider>
          </UnifiedOrdersServiceProvider>
        </DrfqDependenciesProvider>
      </CounterpartiesServiceProvider>
    </GrfqServiceProvider>
  );
}
